<script setup>
import { onMounted, ref, watch, nextTick } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";

const router = useRouter();
const $loading = useLoading(loaderData);
const route = useRoute("organizations-view-id");
const currentTab = ref(0);

const organizationData = ref({
    name: "",
    description: "",
    phone: "",
    address_line_1: "",
    address_line_2: "",
    country_id: "",
    state_id: "",
    zip_code: "",
    branch_id: "",
    values: [],
});

const getOrganizationData = function () {
    let id = route.params.id;
    let loader = $loading.show();
    let url = `/organizations/${id}?show=all`;
    axios
        .get(url)
        .then((response) => {
            if (response.status == "success") {
                organizationData.value = response.data.organization;
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
        });
}

onMounted(async () => {
    getOrganizationData();
});

const discardChanges = () => {
  router.push({ name: "organizations-list" });
}

const editOrganization = () => {
    router.push({ name: "organizations-id", params: { id: organizationData.id } });
}

const resolveIconForFieldType = (system_form_field) => {
    if (system_form_field.type == "single_line") {
        return "tabler-cursor-text";
    } else if (system_form_field.type == "multi_line") {
        return "tabler-forms";
    } else if (system_form_field.type == "integer") {
        return "tabler-123";
    } else if (system_form_field.type == "percent") {
        return "tabler-percentage";
    } else if (system_form_field.type == "decimal") {
        return "tabler-decimal";
    } else if (system_form_field.type == "currency") {
        return "tabler-cash";
    } else if (system_form_field.type == "date") {
        return "tabler-calendar-month";
    } else if (system_form_field.type == "date_time") {
        return "tabler-calendar-clock";
    } else if (system_form_field.type == "email") {
        return "tabler-mail";
    } else if (system_form_field.type == "phone") {
        return "tabler-phone";
    } else if (system_form_field.type == "pick_list") {
        return "tabler-checkup-list";
    } else if (system_form_field.type == "multi_select") {
        return "tabler-list-check";
    } else {
        return "";
    }
};

definePage({ meta: { requiresAuth: true } });
</script>

<template>
    <div>
        <div>
            <!-- 👉 Header  -->
            <div class="d-flex justify-space-between align-center flex-wrap gap-y-4 mb-6">
                <div>
                    <div class="d-flex gap-2 align-center mb-2 flex-wrap">
                        <h2>
                            <strong class="oswald">Organization Details</strong>
                        </h2>
                    </div>
                    
                    <div>
                        <span class="lato-light">
                            {{ new Date(organizationData.created_at).toLocaleString('en-US', {
                                dateStyle: 'long', timeStyle:
                                    'short'
                            }) }}
                        </span>
                    </div>
                </div>
                <div class="d-flex gap-4">
                    <VBtn variant="tonal" color="dark" @click="discardChanges">
                        Back
                    </VBtn>

                    <VBtn variant="tonal" color="primary" @click="editOrganization()">
                        Edit Organization
                    </VBtn>
                </div>
            </div>
            <!-- 👉 Customer Profile  -->
            <VRow>
                <VCol cols="12" md="5" lg="4">
                    <VRow>
                        <!-- SECTION Customer Details -->
                        <VCol cols="12">
                            <VCard>
                                <VCardText class="text-center pt-15">
                                    <!-- 👉 Avatar -->
                                    <VAvatar rounded :size="100" :color="'primary'" :variant="'tonal'">
                                        <VImg v-if="organizationData.user_profile" :src="organizationData.user_profile" />
                                        <span v-else class="text-5xl font-weight-medium">
                                            {{ organizationData.name }}
                                        </span>
                                    </VAvatar>

                                    <h2 class="mt-4">
                                        <strong class="oswald">{{ organizationData.name }}</strong>
                                    </h2>
                                    <span class="text-sm lato-light">{{ organizationData.phone }}</span>
                                    
                                </VCardText>
                            </VCard>
                        </VCol>
                        <!-- !SECTION -->
                    </VRow>
                </VCol>
                <VCol cols="12" md="7" lg="8">
                    <VTabs class="v-tabs-pill mb-3 disable-tab-transition" v-model="currentTab">
                        <VTab :key="0">
                            <span>General</span>
                        </VTab>
                        <VTab :key="1">
                            <span>Additional</span>
                        </VTab>
                    </VTabs>
                    <VWindow class="disable-tab-transition" :touch="false" v-model="currentTab">
                        <VWindowItem>
                            <VCard class="pa-5">
                                <VRow class="parent">
                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Name
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData.name }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                    

                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Phone
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData.phone }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>


                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Address Line 1
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData.address_line_1 }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Address Line 2
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData.address_line_2 }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Country
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData?.country?.name }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                State
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData?.state?.name }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Zip Code
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData.zip_code }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                    <VCol class="child" cols="12" md="4">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Branch
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData?.branch?.branch_name }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                    <VCol class="child" cols="12" md="12">
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                Description
                                            </h3>
                                            <div>
                                                <span class="text-primary lato-light">{{ organizationData.description }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>

                                </VRow>
                            </VCard>
                        </VWindowItem>

                        <VWindowItem>
                            <VCard class="pa-5">
                                <VRow class="parent">
                                    <VCol class="child" cols="12" md="4" 
                                        v-for="ff in organizationData.custom_fields"
                                        :key="ff.form_field.id"
                                    >
                                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 ">
                                            <h3 class="oswald">
                                                {{ ff?.form_field?.name }}
                                            </h3>
                                            <div>
                                                
                                                <span class="text-primary lato-light">{{ Array.isArray(ff.value) ? ff.value.join(', ') : ff.value }}</span>
                                            </div>
                                        </VCardText>
                                    </VCol>
                                </VRow>
                            </VCard>
                        </VWindowItem>
                    </VWindow>
                </VCol>
            </VRow>
        </div>
    </div>
</template>
